<script setup lang="ts">
import { Plus, RefreshCw } from '@lucide/vue'
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, onUnmounted, ref, shallowRef, useId } from 'vue'
import {
    AGENT_CONNECTION_DESCRIPTION_MAX,
    AGENT_CONNECTION_NAME_MAX,
    AGENT_CONNECTION_USAGE_IDS_MAX,
    AGENT_PROVIDER_SUBSCRIPTION_PLANS,
    AGENT_SUBSCRIPTION_PRICES,
    type AgentConnection,
    type AgentConnectionsResponse,
    type AgentProvider,
    type AgentSubscriptionPlan,
    type AgentUsage,
    type AgentUsageByConnection,
    api,
} from '@/api'
import AgentConnectionCard from '@/components/AgentConnectionCard.vue'
import AgentKeys from '@/components/AgentKeys.vue'
import ListSearchField from '@/components/ListSearchField.vue'
import {
    AGENT_PROVIDER_NAMES,
    AGENT_PROVIDERS,
    AGENT_SUBSCRIPTIONS,
    agentAuthLabel,
    hasSubscription,
} from '@/lib/agents'
import { SUBSCRIPTION_PLAN_NAMES, subscriptionLabel } from '@/lib/cost'

const props = defineProps<{
    page?: boolean
    layer?: 'enterprise' | 'team' | 'member'
    layerId?: string
    /**
     * A connection to open in its editor once the list has loaded (#1719): the Blockers row of a lost Claude login
     * sends the person here to replace its credential in place. An id the list does not hold opens nothing.
     */
    reconnect?: string
}>()

const inventory = ref<AgentConnectionsResponse | null>(null)
const loading = ref(true)
const refreshing = ref(false)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)
const search = ref('')
const scope = ref('all')
const mutating = ref(false)
const editorBusy = ref(false)
const form = ref<{ mode: 'add' | 'edit'; provider?: AgentProvider; connectionId?: string } | null>(null)
const owner = ref('')
/** What this credential is for, in the operator's words. Blank keeps the provider name as the label. */
const name = ref('')
const description = ref('')
const priority = ref(0)
/**
 * A subscription login's plan, which prices its runs as a share of the seat. Blank leaves it to detection from
 * Anthropic's profile; a choice here is the operator's and detection never overwrites it.
 */
const plan = ref<AgentSubscriptionPlan | ''>('')
/**
 * Monthly seat price in dollars. Blank takes the plan's list price; Team, Enterprise and Custom need one.
 * A number input's `v-model` hands back a number once something is typed, so it is read through `priceText`.
 */
const monthlyPrice = ref<string | number>('')
const priceText = computed(() => String(monthlyPrice.value ?? '').trim())
/**
 * The kit Select cannot hold '' as a value, so "Detect from Anthropic" is this stand-in inside the control alone.
 * It maps back to '' here, so what is saved is exactly what the native picker saved.
 */
const DETECT_PLAN = '__detect__'
const planChoice = computed(() => plan.value || DETECT_PLAN)
function choosePlan(choice: unknown): void {
    plan.value = choice === DETECT_PLAN ? '' : (planOptions.value.find(option => option === choice) ?? plan.value)
}
/** Each picker only takes a choice it offers; anything else set on its field leaves the screen as it is. */
function chooseOwner(choice: unknown): void {
    owner.value = addTargets.value.map(targetKey).find(key => key === choice) ?? owner.value
}
const SCOPES = ['all', 'enterprise', 'team', 'member'] as const
function chooseScope(choice: unknown): void {
    scope.value = SCOPES.find(option => option === choice) ?? scope.value
}
function planOptionLabel(option: AgentSubscriptionPlan): string {
    const price = AGENT_SUBSCRIPTION_PRICES[option]
    return `${SUBSCRIPTION_PLAN_NAMES[option]}${price ? ` · $${price / 100}/mo` : ''}`
}
/** Ids for the words that name each kit Select's trigger; the panel can be mounted more than once on a page. */
const fieldId = useId()
/** The editor is a kit panel, so it is reached through the component's root element. */
const editor = ref<InstanceType<typeof ZhPanel> | null>(null)
const displayOrder = ref<string[]>([])
const draggingId = ref<string | null>(null)
const dragSnapshot = ref<string[] | null>(null)
const previewPriorities = ref(new Map<string, number>())
let generation = 0

onMounted(load)
onUnmounted(() => {
    generation += 1
})

async function load() {
    const current = ++generation
    refreshing.value = true
    error.value = null
    try {
        const result = await api.agentConnections()
        if (current === generation) {
            inventory.value = result
            displayOrder.value = result.connections.map(connection => connection.id)
            previewPriorities.value = new Map()
            loadUsage(result.connections)
            openReconnect(result.connections)
        }
    } catch {
        if (current === generation) error.value = 'Could not load agent connections. Try again.'
    } finally {
        if (current === generation) {
            loading.value = false
            refreshing.value = false
        }
    }
}

/**
 * Each connected row's usage, keyed by connection id. The list asks for all of them in batched requests of at most
 * `AGENT_CONNECTION_USAGE_IDS_MAX` ids (OS-PORTAL-2), never once per card, and hands each card its own.
 */
const usage = shallowRef(new Map<string, AgentUsage>())
/** The connections whose usage is being read right now: their cards show the meters' loading state. */
const usageLoading = shallowRef(new Set<string>())
/** The newest request each connection's usage waits on. An older request that answers later is ignored for it. */
const usageRequest = new Map<string, number>()
let usageGeneration = 0
const USAGE_FAILED: AgentUsage = { kind: 'unavailable', quotas: [], balance: null, message: 'Could not load usage.' }

/**
 * Asks for the usage of every connected row in this panel's scope, on every inventory load: the first one, Refresh
 * and the reload after a change. So Refresh retries a read that failed or came back unavailable and brings an
 * unchanged balance up to date, as a card did when it asked alone; the API's per-connection usage cache keeps the
 * repeat cheap. A row that is not connected, or is no longer listed, is shown no meters and never asked for.
 */
function loadUsage(connections: AgentConnection[]): void {
    const wanted = connections.filter(row => matchesScope(row) && row.status === 'connected').map(row => row.id)
    const keep = new Set(wanted)
    const nextUsage = new Map([...usage.value].filter(([id]) => keep.has(id)))
    const nextLoading = new Set([...usageLoading.value].filter(id => keep.has(id)))
    for (const id of [...usageRequest.keys()]) {
        if (!keep.has(id)) usageRequest.delete(id)
    }
    const batches: { ids: string[]; request: number }[] = []
    for (let start = 0; start < wanted.length; start += AGENT_CONNECTION_USAGE_IDS_MAX) {
        const ids = wanted.slice(start, start + AGENT_CONNECTION_USAGE_IDS_MAX)
        const request = ++usageGeneration
        for (const id of ids) {
            usageRequest.set(id, request)
            nextLoading.add(id)
        }
        batches.push({ ids, request })
    }
    // Committed before any request is sent: one that fails at once must find its cards already loading.
    usage.value = nextUsage
    usageLoading.value = nextLoading
    for (const { ids, request } of batches) void readUsage(ids, request)
}

/**
 * One batched request. Each card it was asked for, and still waits on it, gets its own usage; one the answer leaves
 * out, or every one when the request fails, says "Could not load usage." as a card did when it asked alone.
 */
async function readUsage(ids: string[], request: number): Promise<void> {
    let answer: AgentUsageByConnection | null = null
    try {
        answer = await api.agentConnectionsUsage(ids)
    } catch {
        answer = null
    }
    const current = ids.filter(id => usageRequest.get(id) === request)
    if (!current.length) return
    const nextUsage = new Map(usage.value)
    const nextLoading = new Set(usageLoading.value)
    for (const id of current) {
        usageRequest.delete(id)
        nextUsage.set(id, (answer && Object.hasOwn(answer, id) ? answer[id] : undefined) ?? USAGE_FAILED)
        nextLoading.delete(id)
    }
    usage.value = nextUsage
    usageLoading.value = nextLoading
}

function targetKey(target: { layer: string; layerId: string }) {
    return `${target.layer}:${target.layerId}`
}

function matchesScope(target: { layer: string; layerId: string }) {
    if (!props.layer) return true
    if (target.layer !== props.layer) return false
    return !props.layerId || target.layerId === props.layerId
}

const scopedTargets = computed(() => inventory.value?.targets.filter(matchesScope) ?? [])
const addTargets = computed(() => scopedTargets.value)
const selectedTarget = computed(() => inventory.value?.targets.find(target => targetKey(target) === owner.value))
const availableProviders = computed(() => (selectedTarget.value ? AGENT_PROVIDERS : []))
const busy = computed(() => mutating.value || editorBusy.value)
const editedRow = computed(() => inventory.value?.connections.find(row => row.id === form.value?.connectionId) ?? null)
/** Only a subscription login bills a seat; keys are billed per token and have no plan. */
const billsSeat = computed(() => editedRow.value?.authKind === 'oauth')
/** The plans the edited login's own provider sells: a ChatGPT login is never offered a Claude plan. */
const planOptions = computed((): readonly AgentSubscriptionPlan[] =>
    editedRow.value ? AGENT_PROVIDER_SUBSCRIPTION_PLANS[editedRow.value.provider] : [],
)
/** Who the plan is read from when nobody sets it, or null for a provider whose login does not say (Grok). */
const planDetectedFrom = computed(() => {
    const provider = editedRow.value?.provider
    return provider && provider !== 'grok' && hasSubscription(provider) ? AGENT_SUBSCRIPTIONS[provider].vendor : null
})
const planNeedsPrice = computed(
    () => plan.value !== '' && AGENT_SUBSCRIPTION_PRICES[plan.value] === undefined && priceText.value === '',
)
const priorityValid = computed(() => Number.isInteger(priority.value) && priority.value >= 0 && priority.value <= 99)
const detectedPlan = computed(() =>
    editedRow.value?.subscription?.source === 'detected' ? subscriptionLabel(editedRow.value.subscription) : null,
)
const scopedConnections = computed(() => (inventory.value?.connections ?? []).filter(matchesScope))
const orderIndex = computed(() => new Map(displayOrder.value.map((id, index) => [id, index])))
const rows = computed(() => {
    const query = search.value.trim().toLocaleLowerCase()
    return scopedConnections.value
        .filter(
            row =>
                (scope.value === 'all' || row.layer === scope.value) &&
                (!query ||
                    `${row.name ?? ''} ${row.description ?? ''} ${AGENT_PROVIDER_NAMES[row.provider]} ${row.ownerName} ${row.layer} ${agentAuthLabel(row)}`
                        .toLocaleLowerCase()
                        .includes(query)),
        )
        .sort((left, right) => (orderIndex.value.get(left.id) ?? 0) - (orderIndex.value.get(right.id) ?? 0))
})
const hasReorderableGroups = computed(() =>
    scopedConnections.value.some(row => {
        const count = routingGroup(row).length
        return count > 1 && count <= 100
    }),
)
const emptyCopy = computed(() => {
    if (props.layer === 'team') return 'Add Claude, OpenAI, Grok or OpenRouter shared by this team.'
    if (props.layer === 'member') return "Add Claude, OpenAI, Grok or OpenRouter for this member's own account."
    if (props.layer === 'enterprise') return 'Add Claude, OpenAI, Grok or OpenRouter for the enterprise.'
    return "Add Claude, OpenAI, Grok or OpenRouter for the enterprise, or connect a member's own account."
})

async function focusEditor() {
    await nextTick()
    const element = editor.value?.$el as HTMLElement | undefined
    element?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
    element?.focus({ preventScroll: true })
}

function add() {
    const target = addTargets.value[0]
    if (!target || busy.value) return
    notice.value = null
    owner.value = targetKey(target)
    name.value = ''
    description.value = ''
    priority.value = 0
    plan.value = ''
    monthlyPrice.value = ''
    form.value = { mode: 'add' }
    void focusEditor()
}

function edit(row: AgentConnection) {
    if (busy.value) return
    notice.value = null
    owner.value = targetKey(row)
    name.value = row.name ?? ''
    description.value = row.description ?? ''
    priority.value = row.priority
    const operator = row.subscription?.source === 'operator' ? row.subscription : null
    plan.value = operator?.plan ?? ''
    monthlyPrice.value =
        operator?.monthlyUsdCents != null && operator.monthlyUsdCents !== AGENT_SUBSCRIPTION_PRICES[operator.plan]
            ? String(operator.monthlyUsdCents / 100)
            : ''
    form.value = { mode: 'edit', provider: row.provider, connectionId: row.id }
    void focusEditor()
}

/** Whether the `reconnect` connection has been opened already, so a reload of the list does not reopen it. */
let reconnectOpened = false

/** Opens the editor on the connection `reconnect` names, once, when nothing else is being edited. */
function openReconnect(connections: AgentConnection[]): void {
    if (reconnectOpened || !props.reconnect || form.value) return
    const row = connections.find(connection => connection.id === props.reconnect)
    if (!row) return
    reconnectOpened = true
    edit(row)
}

function closeEditor() {
    if (busy.value) return
    form.value = null
    owner.value = ''
    name.value = ''
    description.value = ''
    priority.value = 0
    plan.value = ''
    monthlyPrice.value = ''
}

function sameRoutingGroup(left: AgentConnection, right: AgentConnection) {
    return left.layer === right.layer && left.layerId === right.layerId
}

function routingGroup(row: AgentConnection) {
    return scopedConnections.value
        .filter(candidate => sameRoutingGroup(candidate, row))
        .sort((left, right) => (orderIndex.value.get(left.id) ?? 0) - (orderIndex.value.get(right.id) ?? 0))
}

function canReorder(row: AgentConnection) {
    const groupSize = routingGroup(row).length
    return !busy.value && !form.value && !search.value.trim() && groupSize > 1 && groupSize <= 100
}

function startReorder(row: AgentConnection, event: DragEvent) {
    if (!canReorder(row)) return
    draggingId.value = row.id
    dragSnapshot.value = [...displayOrder.value]
    previewPriorities.value = new Map()
    event.dataTransfer?.setData('text/plain', row.id)
    if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        const handle = (event.target as HTMLElement | null)?.closest<HTMLElement>('button[draggable="true"]')
        if (handle) event.dataTransfer.setDragImage(handle, handle.offsetWidth / 2, handle.offsetHeight / 2)
    }
}

function moveReorderOver(row: AgentConnection, event: DragEvent) {
    const dragged = scopedConnections.value.find(connection => connection.id === draggingId.value)
    if (!dragged || !sameRoutingGroup(dragged, row)) return
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    if (dragged.id === row.id) return
    const next = [...displayOrder.value]
    const from = next.indexOf(dragged.id)
    const to = next.indexOf(row.id)
    if (from < 0 || to < 0) return
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-connection-id]')
    const bounds = target?.getBoundingClientRect()
    const placeAfter = bounds?.height ? event.clientY > bounds.top + bounds.height / 2 : from < to
    let insertion = to + (placeAfter ? 1 : 0)
    next.splice(from, 1)
    if (from < insertion) insertion -= 1
    if (insertion === from) return
    next.splice(insertion, 0, dragged.id)
    displayOrder.value = next
    previewPriorities.value = new Map(routingGroup(row).map((connection, index) => [connection.id, index]))
}

async function finishReorder(row: AgentConnection, event: DragEvent) {
    const dragged = scopedConnections.value.find(connection => connection.id === draggingId.value)
    if (!dragged || !sameRoutingGroup(dragged, row)) return
    event.preventDefault()
    const snapshot = dragSnapshot.value
    const connectionIds = routingGroup(row).map(connection => connection.id)
    const previousIds = snapshot?.filter(id => connectionIds.includes(id)) ?? []
    draggingId.value = null
    dragSnapshot.value = null
    if (connectionIds.every((id, index) => id === previousIds[index])) {
        previewPriorities.value = new Map()
        return
    }

    mutating.value = true
    error.value = null
    notice.value = null
    try {
        await api.reorderAgentConnections(connectionIds)
        notice.value = 'Agent connection priority updated.'
        await load()
    } catch {
        if (snapshot) displayOrder.value = snapshot
        previewPriorities.value = new Map()
        error.value = 'Could not reorder these agent connections. Try again.'
    } finally {
        mutating.value = false
    }
}

function cancelReorder() {
    if (!draggingId.value) return
    if (dragSnapshot.value) displayOrder.value = dragSnapshot.value
    draggingId.value = null
    dragSnapshot.value = null
    previewPriorities.value = new Map()
}

/**
 * The plan to send, or `undefined` to leave it alone. Clearing only goes back to detection when the operator had
 * set one: a rename must not throw away a plan the provider's own account already filled in.
 */
function subscriptionPatch() {
    if (!billsSeat.value) return
    if (plan.value === '') return editedRow.value?.subscription?.source === 'operator' ? null : undefined
    const dollars = Number(priceText.value)
    const monthlyUsdCents = priceText.value !== '' && Number.isFinite(dollars) ? Math.round(dollars * 100) : undefined
    return monthlyUsdCents === undefined ? { plan: plan.value } : { plan: plan.value, monthlyUsdCents }
}

/**
 * Rename, describe or price without touching the credential. Editing a connection is usually one or the
 * other, and `AgentKeys` cannot save at all without a new key — so the details have their own button.
 */
async function saveDetails() {
    const id = form.value?.connectionId
    if (!id || busy.value || planNeedsPrice.value || !priorityValid.value) return
    mutating.value = true
    error.value = null
    notice.value = null
    try {
        const subscription = subscriptionPatch()
        await api.labelAgentConnection(id, {
            name: name.value,
            description: description.value,
            priority: priority.value,
            ...(subscription === undefined ? {} : { subscription }),
        })
        notice.value = 'Agent connection details saved.'
        form.value = null
        owner.value = ''
        name.value = ''
        description.value = ''
        priority.value = 0
        plan.value = ''
        monthlyPrice.value = ''
        await load()
    } catch {
        error.value = 'Could not save these details. Try again.'
    } finally {
        mutating.value = false
    }
}

async function insertCreatedConnection(
    created: AgentConnection,
    wanted: { name: string; description: string; priority: number },
) {
    const ordered = (inventory.value?.connections ?? []).filter(
        row => sameRoutingGroup(row, created) && row.id !== created.id,
    )
    ordered.splice(Math.min(wanted.priority, ordered.length), 0, created)
    if (ordered.length > 1) await api.reorderAgentConnections(ordered.map(row => row.id))
    if (wanted.name || wanted.description) {
        await api.labelAgentConnection(created.id, {
            name: wanted.name,
            description: wanted.description,
        })
    }
}

async function saved(provider: AgentProvider, connectionId?: string) {
    const wasAdded = form.value?.mode === 'add'
    const wanted = {
        name: name.value.trim(),
        description: description.value.trim(),
        priority: priority.value,
        owner: owner.value,
    }
    form.value = null
    owner.value = ''
    name.value = ''
    description.value = ''
    priority.value = 0
    editorBusy.value = false
    notice.value = 'Agent connection saved.'
    await load()
    // The connection only exists once the credential is stored. Insert a new one at the requested
    // position through the same batch reorder call as drag-and-drop, then attach its optional details.
    const created = connectionId
        ? (inventory.value?.connections ?? []).find(row => row.id === connectionId)
        : (inventory.value?.connections ?? []).find(row => row.provider === provider && targetKey(row) === wanted.owner)
    if (!created) return
    try {
        if (wasAdded) {
            await insertCreatedConnection(created, wanted)
        } else {
            if (!wanted.name && !wanted.description && wanted.priority === 0) return
            await api.labelAgentConnection(created.id, {
                name: wanted.name,
                description: wanted.description,
                priority: wanted.priority,
            })
        }
        await load()
    } catch {
        error.value = 'The connection was saved, but its priority or details could not be. Edit it to try again.'
    }
}

async function remove(row: AgentConnection) {
    if (busy.value) return
    mutating.value = true
    error.value = null
    notice.value = null
    try {
        await api.unbindLlm(row.provider, row.layer, row.layerId, row.id)
        if (owner.value === targetKey(row)) {
            form.value = null
            owner.value = ''
        }
        notice.value = `${AGENT_PROVIDER_NAMES[row.provider]} removed from ${row.ownerName}.`
        await load()
    } catch {
        error.value = 'Could not remove this agent connection. Try again.'
    } finally {
        mutating.value = false
    }
}
</script>

<template>
    <div class="space-y-5">
        <header v-if="page" class="flex flex-wrap items-start justify-between gap-4">
            <div class="space-y-1">
                <ZhPageTitle>Agents</ZhPageTitle>
                <p class="text-muted-foreground text-sm">
                    Manage model connections for the enterprise, its teams and its members in one place.
                </p>
            </div>
            <div class="flex items-center gap-2">
                <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label="Refresh agent connections"
                    :disabled="refreshing || busy"
                    @click="load"
                >
                    <RefreshCw :size="15" :class="refreshing ? 'motion-safe:animate-spin' : ''" />
                </Button>
                <Button size="sm" :disabled="loading || busy || !addTargets.length" @click="add">
                    <Plus :size="15" /> Add Agent
                </Button>
            </div>
        </header>

        <ZhPanel
            v-if="error"
            as="div"
            tone="failed"
            role="alert"
            class="flex flex-wrap items-center justify-between gap-3 text-sm"
        >
            <span>{{ error }}</span>
            <Button variant="outline" size="sm" :disabled="refreshing || busy" @click="load">Try again</Button>
        </ZhPanel>
        <p v-if="notice" role="status" class="text-sm">{{ notice }}</p>

        <ZhPanel
            v-if="form"
            ref="editor"
            as="article"
            tabindex="-1"
            class="space-y-4 outline-none"
            aria-label="Agent connection editor"
        >
            <header class="flex flex-wrap items-center justify-between gap-3">
                <ZhSectionHeading>{{ form.mode === 'edit' ? `Edit ${AGENT_PROVIDER_NAMES[form.provider!]} connection` : 'Add agent connection' }}</ZhSectionHeading>
                <span v-if="form.mode === 'edit'" class="text-muted-foreground text-xs">
                    {{ selectedTarget?.name }} · {{ selectedTarget?.layer }}
                </span>
            </header>
            <!--
                The words above each Select name its trigger; `aria-label` lands on the field the kit Select
                mirrors its choice on.
            -->
            <div v-if="form.mode === 'add' && addTargets.length > 1" class="grid max-w-xl gap-1 text-sm">
                <span :id="`${fieldId}-owner`" class="text-muted-foreground">Connection owner</span>
                <Select
                    :model-value="owner"
                    :disabled="busy"
                    aria-label="Connection owner"
                    @update:model-value="chooseOwner"
                >
                    <SelectTrigger class="w-full" :aria-labelledby="`${fieldId}-owner`">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem v-for="target in addTargets" :key="targetKey(target)" :value="targetKey(target)">
                            {{ target.name }} · {{ target.layer === 'enterprise' ? 'Enterprise' : target.layer === 'team' ? 'Team' : 'Member' }}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div class="grid max-w-xl gap-3 sm:grid-cols-2">
                <label class="block text-sm">
                    <span class="text-muted-foreground">Name <span class="text-xs">(optional)</span></span>
                    <Input
                        v-model="name"
                        type="text"
                        :disabled="busy"
                        :maxlength="AGENT_CONNECTION_NAME_MAX"
                        placeholder="Design team Max seat"
                        class="mt-1"
                    />
                </label>
                <label class="block text-sm">
                    <span class="text-muted-foreground">Description <span class="text-xs">(optional)</span></span>
                    <Input
                        v-model="description"
                        type="text"
                        :disabled="busy"
                        :maxlength="AGENT_CONNECTION_DESCRIPTION_MAX"
                        placeholder="Used for design tasks only"
                        class="mt-1"
                    />
                </label>
                <label class="block text-sm">
                    <span class="text-muted-foreground">Priority</span>
                    <!--
                        No `.number`: the kit Input does not take model modifiers, so one would land on the field as an
                        attribute. Its own v-model already hands back a number from a number field.
                    -->
                    <Input
                        v-model="priority"
                        type="number"
                        min="0"
                        max="99"
                        step="1"
                        :disabled="busy"
                        data-testid="agent-priority"
                        class="mt-1"
                    />
                </label>
            </div>
            <p class="text-muted-foreground text-xs">
                For the same owner, lower priorities are tried first across providers (0–99). If an account has no
                capacity, calls fall back to the next connection until it recovers.
            </p>
            <div v-if="form.mode === 'edit' && billsSeat" class="grid max-w-xl gap-3 sm:grid-cols-2">
                <div class="grid gap-1 text-sm">
                    <span :id="`${fieldId}-plan`" class="text-muted-foreground">Seat plan</span>
                    <Select
                        :model-value="planChoice"
                        :disabled="busy"
                        aria-label="Seat plan"
                        data-testid="agent-seat-plan-select"
                        @update:model-value="choosePlan"
                    >
                        <SelectTrigger class="w-full" :aria-labelledby="`${fieldId}-plan`">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem :value="DETECT_PLAN">
                                {{ detectedPlan ? `Detected: ${detectedPlan}` : planDetectedFrom ? `Detect from ${planDetectedFrom}` : 'Not set' }}
                            </SelectItem>
                            <SelectItem v-for="option in planOptions" :key="option" :value="option">
                                {{ planOptionLabel(option) }}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <label v-if="plan !== ''" class="block text-sm">
                    <span class="text-muted-foreground">
                        Monthly price (USD)
                        <span v-if="AGENT_SUBSCRIPTION_PRICES[plan] !== undefined" class="text-xs">(optional)</span>
                    </span>
                    <Input
                        v-model="monthlyPrice"
                        type="number"
                        min="0"
                        step="1"
                        inputmode="decimal"
                        :disabled="busy"
                        data-testid="agent-seat-price"
                        :placeholder="AGENT_SUBSCRIPTION_PRICES[plan] ? String(AGENT_SUBSCRIPTION_PRICES[plan]! / 100) : 'e.g. 30'"
                        class="mt-1"
                    />
                </label>
                <p class="text-muted-foreground text-xs sm:col-span-2">
                    Runs on this login are costed as their share of the seat: its weekly price times the share of the
                    weekly limit they used. Without a plan they are costed at API list price.
                </p>
            </div>
            <div v-if="form.mode === 'edit'" class="flex flex-wrap gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    :disabled="busy || planNeedsPrice || !priorityValid"
                    @click="saveDetails"
                >
                    Save details
                </Button>
            </div>
            <fieldset :disabled="mutating || !priorityValid" class="min-w-0">
                <AgentKeys
                    v-if="selectedTarget"
                    :key="`${owner}:${form.mode}:${form.provider ?? ''}`"
                    :layer="selectedTarget.layer"
                    :layer-id="selectedTarget.layerId"
                    editor-only
                    :initial-mode="form.mode"
                    :initial-provider="form.provider"
                    :connection-id="form.connectionId"
                    :available-providers="availableProviders"
                    :disabled="mutating || !priorityValid"
                    @saved="saved"
                    @cancel="closeEditor"
                    @busy-change="editorBusy = $event"
                />
            </fieldset>
        </ZhPanel>

        <p v-if="loading" role="status" class="text-muted-foreground py-12 text-center text-sm">
            Loading agent connections…
        </p>
        <template v-else-if="inventory">
            <div v-if="page && inventory.connections.length" class="flex flex-wrap items-end justify-between gap-3">
                <div class="flex flex-wrap items-end gap-3">
                    <label class="block text-sm">
                        <span class="text-muted-foreground">Search connections</span>
                        <ListSearchField v-model="search" type="search" placeholder="Provider or owner" class="mt-1 sm:w-64" />
                    </label>
                    <div class="grid gap-1 text-sm">
                        <span :id="`${fieldId}-scope`" class="text-muted-foreground">Scope</span>
                        <Select :model-value="scope" aria-label="Scope" @update:model-value="chooseScope">
                            <SelectTrigger class="min-w-40" :aria-labelledby="`${fieldId}-scope`">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All connections</SelectItem>
                                <SelectItem value="enterprise">Enterprise</SelectItem>
                                <SelectItem value="team">Team</SelectItem>
                                <SelectItem value="member">Members</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <p class="text-muted-foreground text-xs">
                    {{ rows.length }} of {{ inventory.connections.length }} connections
                </p>
            </div>
            <!-- On the Agents page, the empty state's one action opens the same editor the header's Add Agent opens. -->
            <ZhEmptyState v-if="!scopedConnections.length" label="No agent connections yet">
                {{ emptyCopy }}
                <template v-if="page && addTargets.length" #action>
                    <Button size="sm" :disabled="loading || busy" @click="add"><Plus :size="15" /> Add Agent</Button>
                </template>
            </ZhEmptyState>
            <ZhEmptyState v-else-if="!rows.length">No connections match your search or scope.</ZhEmptyState>
            <p v-if="hasReorderableGroups" class="text-muted-foreground text-xs">
                {{ search.trim() ? 'Clear search to reorder connections.' : 'Drag the grip to reorder accounts for the same owner across providers.' }}
            </p>
            <!-- A reordered row slides into place on the kit's UI motion step, and holds still for reduced motion. -->
            <TransitionGroup
                v-if="rows.length"
                tag="ul"
                move-class="motion-safe:transition-transform motion-safe:duration-(--motion-ui) motion-safe:ease-out"
                class="space-y-3"
                aria-label="Agent connections"
            >
                <AgentConnectionCard
                    v-for="row in rows"
                    :key="row.id"
                    :row="row"
                    :busy="busy"
                    :show-owner="page"
                    :reorderable="canReorder(row)"
                    :dragging="draggingId === row.id"
                    :display-priority="previewPriorities.get(row.id)"
                    :usage="usage.get(row.id) ?? null"
                    :usage-loading="usageLoading.has(row.id)"
                    @edit="edit"
                    @delete="remove"
                    @dragstart="startReorder"
                    @dragover="moveReorderOver"
                    @drop="finishReorder"
                    @dragend="cancelReorder"
                />
            </TransitionGroup>
            <div v-if="!page && !form && addTargets.length" class="flex justify-start">
                <Button size="sm" variant="outline" :disabled="loading || busy" @click="add">
                    <Plus :size="15" /> Add Agent
                </Button>
            </div>
            <p v-if="page" class="text-muted-foreground text-xs">
                An exact task model is tried first. Otherwise, connections fall back from member to team to enterprise, by priority within each scope. Keys are
                never shown, only their last four characters.
            </p>
        </template>
    </div>
</template>
